import React, { FunctionComponent, ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  ButtonStandard,
  ButtonStandardType
} from 'record-it-components/button/ButtonStandard/ButtonStandard';
import {
  CardListItem,
  CardListItemSize
} from 'record-it-components/cardList/CardListItem/CardListItem';
import { CardListItemIconButton } from 'record-it-components/cardList/CardListItemIconButton/CardListItemIconButton';
import { CardListItemTitle } from 'record-it-components/cardList/CardListItemTitle/CardListItemTitle';
import { useEventCallback } from 'record-it-components/hooks/useEventCallback';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import { EntityName } from 'record-it-time-common/EntityName';
import { iconConfigurations } from '../../../classes/icon/iconConfigurations';
import { RoutePaths } from '../../../classes/RoutePaths/RoutePaths';
import { useRequiredWorkTimeEntryToCreate } from '../../../hooks/createWorkTimeEntry/useRequiredWorkTimeEntryToCreate';
import { useEntityByIdOrOriginalId } from '../../../hooks/entity/useEntityByIdOrOriginalId';
import { useAppDispatch } from '../../../store/hooks';
import { createWorkTimeEntrySlice } from '../../../store/slice/createWorkTimeEntry/createWorkTimeEntrySlice';
import styles from './WorkTimeEntryToCreateConstructionSiteWidget.module.css';

export type WorkTimeEntryToCreateConstructionSiteWidgetProps =
  PropsWithoutChildren<ComponentProps<'div'>>;

export const WorkTimeEntryToCreateConstructionSiteWidget: FunctionComponent<WorkTimeEntryToCreateConstructionSiteWidgetProps> =
  React.memo((props) => {
    const { ...remainingProps } = prepareProps({ props, ignoreChildren: true });

    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const workTimeEntryToCreate = useRequiredWorkTimeEntryToCreate();
    const selectedConstructionSite = useEntityByIdOrOriginalId(
      EntityName.ConstructionSite,
      workTimeEntryToCreate.constructionSiteId
    );

    const handleRemoveSelectedConstructionSiteClick = useEventCallback(() => {
      dispatch(createWorkTimeEntrySlice.actions.setConstructionSiteId(null));
    });

    const handleSelectConstructionSiteClick = useEventCallback(() => {
      navigate(RoutePaths.createWorkTimeEntrySelectConstructionSite());
    });

    return (
      <div {...remainingProps}>
        <div className={styles.title}>
          {t(
            'components.workTime.WorkTimeEntryToCreateConstructionSiteWidget.title'
          )}
        </div>

        {workTimeEntryToCreate.constructionSiteId ? (
          <CardListItem
            size={CardListItemSize.Small}
            inline={true}
            data-testid="WorkTimeEntryBreakListItem.SelectedConstructionSite"
          >
            <CardListItemTitle>
              {selectedConstructionSite
                ? selectedConstructionSite.name
                : t(
                    'components.workTime.WorkTimeEntryToCreateConstructionSiteWidget.unknownConstructionSite'
                  )}
            </CardListItemTitle>
            <CardListItemIconButton
              {...iconConfigurations.prefixed.listItem.deleteIcon}
              onClick={handleRemoveSelectedConstructionSiteClick}
              data-testid="WorkTimeEntryToCreateConstructionSiteWidget.RemoveSelectedConstructionSite"
            ></CardListItemIconButton>
          </CardListItem>
        ) : null}

        <div className={styles.selectConstructionSiteButtonWrapper}>
          <ButtonStandard
            type={ButtonStandardType.Primary}
            onClick={handleSelectConstructionSiteClick}
            data-testid="WorkTimeEntryToCreateConstructionSiteWidget.SelectConstructionSiteButton"
          >
            {t(
              'components.workTime.WorkTimeEntryToCreateConstructionSiteWidget.selectConstructionSite',
              {
                context: workTimeEntryToCreate.constructionSiteId
                  ? 'constructionSiteAlreadySelected'
                  : null
              }
            )}
          </ButtonStandard>
        </div>
      </div>
    );
  });
